/*
 * One hit, drawn the way the message list draws a message: a face, who wrote it, where it is, when
 * it was written, and the text with the matched runs marked. The whole row is the link — a result
 * list is a list of places to go, and a person aiming at a two-line excerpt is a person missing it.
 *
 * The row is here rather than in the page because two callers draw it: the page itself, for a kind
 * no module claimed, and a module's own renderer (`searchResultRenderers`), which adds the two
 * facts core cannot know — that this hit is a message, and whose face belongs on it. What each
 * caller knows differs; the shape a reader scans must not.
 *
 * The address is handed in as text. Core has the conversation's name from the routes layer's
 * locator, a module has it from its own model; neither is the row's business.
 *
 * The excerpt goes through `emojiText`, because it is a string the server cut out of a message and
 * not markup the renderer parsed: a 🎉 in it reached the screen as a tofu box on every machine whose
 * system font has no colour emoji (X-A1 D12).
 *
 * A row says why it is on the screen. Where the excerpt carries no marked run the reason is somewhere
 * else — the query matched the name of a file on the message — and the row names the files under the
 * excerpt instead of leaving a reader to guess (X-B4 B4-15). Only the caller knows them, so they are
 * handed in like the place.
 */

import type { ReactNode } from "react"
import { emojiText } from "../../ui/index.ts"
import { formatDateTime, formatDayTime, formatList, isoDateTime } from "../i18n/format.ts"
import { t } from "../i18n/index.ts"
import { useNow } from "../i18n/useNow.ts"
import { FLAG_FILE, type SearchResult } from "./api.ts"
import "./hitRow.css"

export interface HitRowProps {
	result: SearchResult
	/** Where the hit lives, or null when this build cannot address that kind of hit. */
	href: string | null
	/** In-place navigation for the row's link; without it the link is an ordinary one. */
	onNavigate?: ((path: string) => void) | undefined
	/** Where the hit is, already named: `#allgemein`, a person's name, or the sentence for a scope
	 *  nothing can name. */
	place: string
	/**
	 * False when `place` is that sentence rather than a name. The sentence already says the reader is
	 * not in the conversation, so the *Not joined* pill beside it would say it twice.
	 */
	placeNamed?: boolean | undefined
	/** The names of the files on the message, where the caller holds them and the match was one. */
	files?: readonly string[] | undefined
	/** The author's face, when the caller knows whose hit this is. */
	avatar?: ReactNode
	/** The author's name, for the same reason. */
	author?: string
}

export function HitRow({
	result,
	href,
	onNavigate,
	place,
	placeNamed = true,
	files,
	avatar,
	author,
}: HitRowProps) {
	// The parts are runs of one string and have no identity of their own; where a run sits in that
	// string is the only key there is.
	const parts = result.snippet.parts
	// The stamp is the product's own — "10:18" today, "Yesterday 17:40" before that — and it only
	// changes at midnight, so the clock it reads ticks by the hour rather than by the minute. The
	// absolute date stays in the `title`, for the reader who wants it.
	const now = useNow(3_600_000)

	// A snippet can be empty — the excerpt is cut from the message's plain text, and a message that
	// is only a code block has none. The row then says so in words rather than becoming an empty
	// link: a link with no content is nothing to click and nothing to read out (WCAG 2.4.4).
	const snippet =
		parts.length === 0 ? (
			<span className="tb-search__nosnippet">{t("search.hit.noSnippet")}</span>
		) : (
			parts.map((part, index) =>
				part.m === true ? (
					<mark key={index}>{emojiText(part.t)}</mark>
				) : (
					<span key={index}>{emojiText(part.t)}</span>
				),
			)
		)

	return (
		<li className="tb-search__hit">
			{avatar}
			<div className="tb-search__body">
				<div className="tb-search__head">
					{author === undefined ? null : <span className="tb-search__author">{author}</span>}
					<span className="tb-search__place">{place}</span>
					<time
						className="tb-search__when"
						dateTime={isoDateTime(result.createdAtMs)}
						title={formatDateTime(result.createdAtMs)}
					>
						{formatDayTime(result.createdAtMs, now)}
					</time>
					{/* What a row says about itself beside the time: that it carries a file, and that its
					    conversation is one the reader is not in. Both are one-word facts, which is what a
					    pill is (§6.9). */}
					{(result.flags & FLAG_FILE) === 0 ? null : (
						<span className="tb-pill">{t("search.hit.hasFile")}</span>
					)}
					{result.joined || !placeNamed ? null : (
						<span className="tb-pill">{t("search.hit.notJoined")}</span>
					)}
				</div>
				{href === null ? (
					<p className="tb-search__snippet">{snippet}</p>
				) : (
					<a
						// The link covers the row (hitRow.css): the excerpt names it, and the rest of the
						// row is the same target rather than dead space beside it.
						className="tb-search__snippet tb-search__link"
						href={href}
						// The excerpt is the link's name wherever there is one; the fallback names the
						// link only when there is no text to name it with.
						aria-label={parts.length === 0 ? t("search.hit.open") : undefined}
						onClick={(event) => {
							if (event.metaKey || event.ctrlKey || event.shiftKey || onNavigate === undefined) {
								return
							}
							event.preventDefault()
							onNavigate(href)
						}}
					>
						{snippet}
					</a>
				)}
				{/* Why a row with nothing marked in it is a hit: the query matched one of these names. It
				    stands under the excerpt and outside the link, so the link keeps the excerpt as its
				    name. */}
				{files === undefined || files.length === 0 ? null : (
					<p className="tb-search__files">
						{t("search.hit.files", { names: formatList([...files]) })}
					</p>
				)}
			</div>
			{href === null ? null : (
				// What the row does when it is clicked, shown while the pointer is on it. The link
				// already says it to a screen reader: this is the same sentence for the eye, and it stays
				// a span rather than becoming a second control for the one target it describes.
				<span className="tb-search__view" aria-hidden="true">
					{t("search.hit.view")}
				</span>
			)}
		</li>
	)
}
